/**
 * Labeled registry metadata fields for expanded artifact detail views
 */

import React from 'react';
import {
  extractRegistryMeta,
  formatDocTypeLabel,
  formatRegistryColumnLabel,
} from '../../utils/registryArtifactUtils';
import type { Artifact } from '../../types/api';
import { RegistryMetaBadges } from './RegistryMetaBadges';

interface RegistryMetaDetailGridProps {
  artifact: Artifact;
  className?: string;
}

export function RegistryMetaDetailGrid({ artifact, className = '' }: RegistryMetaDetailGridProps) {
  const reg = extractRegistryMeta(artifact);
  if (!reg) return null;

  return (
    <div className={className}>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-600">
        Registry classification
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
        {reg.project_id && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Project ID</div>
            <div className="font-mono text-xs font-medium text-indigo-900">{reg.project_id}</div>
          </div>
        )}
        {reg.project_name && (
          <div className="col-span-2 rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Project</div>
            <div className="text-xs font-medium text-indigo-900">{reg.project_name}</div>
          </div>
        )}
        {reg.doc_type && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Doc Type</div>
            <div className="font-medium text-indigo-900">{formatDocTypeLabel(reg.doc_type)}</div>
          </div>
        )}
        {reg.registry_column && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Registry Column</div>
            <div className="font-medium text-indigo-900">
              {formatRegistryColumnLabel(reg.registry_column)}
            </div>
          </div>
        )}
        {reg.client && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Client</div>
            <div className="font-medium text-indigo-900">{reg.client}</div>
          </div>
        )}
        {reg.geography && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Geography</div>
            <div className="font-medium text-indigo-900">{reg.geography}</div>
          </div>
        )}
        {reg.service_line && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Service Line</div>
            <div className="font-medium text-indigo-900">{reg.service_line}</div>
          </div>
        )}
        {reg.sector && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Sector</div>
            <div className="font-medium text-indigo-900">{reg.sector}</div>
          </div>
        )}
        {reg.contract_value && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Contract Value</div>
            <div className="font-medium text-indigo-900">{reg.contract_value}</div>
          </div>
        )}
        {reg.start_date && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Start Date</div>
            <div className="text-xs font-medium text-indigo-900">{reg.start_date}</div>
          </div>
        )}
        {reg.end_date && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">End Date</div>
            <div className="text-xs font-medium text-indigo-900">{reg.end_date}</div>
          </div>
        )}
        {reg.registry_source && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Registry Source</div>
            <div className="text-xs font-medium text-indigo-900">{reg.registry_source}</div>
          </div>
        )}
        {reg.entity_type && (
          <div className="rounded bg-indigo-50 px-3 py-2">
            <div className="mb-0.5 text-xs text-indigo-600">Entity Type</div>
            <div className="text-xs font-medium text-indigo-900">{reg.entity_type}</div>
          </div>
        )}
        <div className="col-span-2 rounded bg-indigo-50 px-3 py-2 md:col-span-4">
          <RegistryMetaBadges artifact={artifact} />
        </div>
      </div>
    </div>
  );
}
